<template>
  <div>
    <Header />
  <v-container fluid>
    <v-card>
      <v-card-title>
        <h2>Fehlgeschlagene Labels</h2>
        <v-spacer></v-spacer>
        <v-btn
          v-if="selectedOrders.length > 0"
          color="success"
          @click="bulkRetryLabels"
          :loading="bulkRetrying"
          class="mr-2"
        >
          <v-icon left>mdi-refresh-circle</v-icon>
          {{ selectedOrders.length }} Auftrag{{ selectedOrders.length > 1 ? 'e' : '' }} wiederholen
        </v-btn>
        <v-btn
          color="primary"
          @click="refreshData"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Aktualisieren
        </v-btn>
      </v-card-title>

      <!-- Filters -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.startDate"
                  label="Startdatum"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="filters.startDate = null"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.startDate"
                @input="startDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          
          <v-col cols="12" md="3">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.endDate"
                  label="Enddatum"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="filters.endDate = null"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.endDate"
                @input="endDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.errorType"
              :items="errorTypes"
              label="Fehlertyp"
              clearable
              dense
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.shop"
              :items="shops"
              label="Shop"
              clearable
              dense
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              @click="applyFilters"
              block
            >
              Filter anwenden
            </v-btn>
            <v-btn
              text
              @click="clearFilters"
              block
              class="mt-2"
            >
              Filter zurücksetzen
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        v-model="selectedOrders"
        :headers="headers"
        :items="orders"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalOrders"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100]
        }"
        show-select
        item-key="orderId"
        class="elevation-1"
      >
        <!-- Order ID -->
        <template v-slot:item.orderId="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="font-weight-medium"
              >
                {{ item.orderId }}
              </span>
            </template>
            <span>Managed ID: {{ item.managedId }}</span>
          </v-tooltip>
        </template>

        <!-- Shop -->
        <template v-slot:item.shop="{ item }">
          <v-chip
            :color="getShopColor(item.shop)"
            small
            dark
          >
            {{ item.shop }}
          </v-chip>
        </template>

        <!-- Customer -->
        <template v-slot:item.customer="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.customer.name }}</div>
            <div class="text-caption">
              {{ item.customer.zip }} {{ item.customer.city }}, {{ item.customer.country }}
            </div>
          </div>
        </template>

        <!-- Products -->
        <template v-slot:item.products="{ item }">
          <v-chip
            v-for="(product, index) in item.products.slice(0, 2)"
            :key="index"
            small
            class="ma-1"
          >
            {{ product.sku }} ({{ product.quantity }})
          </v-chip>
          <v-chip
            v-if="item.products.length > 2"
            small
            class="ma-1"
          >
            +{{ item.products.length - 2 }} more
          </v-chip>
        </template>

        <!-- Error Reason -->
        <template v-slot:item.errorReason="{ item }">
          <v-tooltip bottom max-width="400">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="text-truncate"
                style="max-width: 250px;"
              >
                <v-icon
                  small
                  color="error"
                  class="mr-1"
                >
                  mdi-alert-circle
                </v-icon>
                {{ item.errorReason }}
              </div>
            </template>
            <span>{{ item.errorReason }}</span>
          </v-tooltip>
        </template>

        <!-- Shipping Type -->
        <template v-slot:item.shipping="{ item }">
          <v-chip
            :color="item.shipping.isPrime ? 'primary' : 'default'"
            small
          >
            {{ item.shipping.type }}
          </v-chip>
        </template>

        <!-- Create Date -->
        <template v-slot:item.createDate="{ item }">
          {{ formatDate(item.createDate) }}
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            @click="viewDetails(item)"
            color="primary"
          >
            <v-icon>mdi-eye</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="retryLabel(item)"
            color="success"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Quick Retry Dialog -->
    <v-dialog
      v-model="quickRetryDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Label-Erstellung wiederholen
        </v-card-title>
        <v-card-text>
          <p>Möchten Sie die Label-Erstellung für Bestellung {{ retryOrder?.orderId }} erneut versuchen?</p>
          <v-alert
            type="info"
            dense
            class="mt-3"
          >
            Dies versucht, das Label mit den vorhandenen Bestelldaten zu erstellen. Für erweiterte Optionen öffnen Sie die Bestelldetails.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="quickRetryDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmQuickRetry"
            :loading="retrying"
          >
            Wiederholen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Retry Progress Dialog -->
    <v-dialog
      v-model="bulkRetryDialog"
      max-width="600"
      persistent
    >
      <v-card>
        <v-card-title>
          <h3>Bulk Label-Erstellung</h3>
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            :value="bulkRetryProgressPercent"
            color="primary"
            height="25"
            class="mb-4"
          >
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}%</strong>
            </template>
          </v-progress-linear>
          
          <div class="text-center mb-4">
            <div class="text-h6">{{ bulkRetryProgress.current }} / {{ bulkRetryProgress.total }}</div>
            <div v-if="bulkRetryProgress.currentOrder" class="text-subtitle-1">
              Verarbeite: {{ bulkRetryProgress.currentOrder.orderId }}
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div class="results-container" style="max-height: 300px; overflow-y: auto;">
            <v-list dense>
              <v-list-item
                v-for="(result, index) in bulkRetryProgress.results"
                :key="index"
                :class="result.success ? 'success--text' : 'error--text'"
              >
                <v-list-item-icon>
                  <v-icon
                    :color="result.success ? 'success' : 'error'"
                    small
                  >
                    {{ result.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ result.orderId }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="!result.success">
                    {{ result.error }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeBulkRetryDialog"
            :disabled="bulkRetrying"
          >
            {{ bulkRetrying ? 'Verarbeitung läuft...' : 'Schließen' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  </div>
</template>

<script>
import Header from './header.vue';
import FailedLabelsService from '@/services/FailedLabelsService';

export default {
  name: 'FailedLabels',
  components: {
    Header
  },
  data() {
    return {
      orders: [],
      loading: false,
      totalOrders: 0,
      isInitialLoad: true,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['createDate'],
        sortDesc: [true]
      },
      headers: [
        { text: '', value: 'data-table-select', width: '50px' },
        { text: 'Bestell-Nr.', value: 'orderId', width: '130px' },
        { text: 'Shop', value: 'shop', width: '100px' },
        { text: 'Kunde', value: 'customer', sortable: false },
        { text: 'Produkte', value: 'products', sortable: false },
        { text: 'Fehlergrund', value: 'errorReason', sortable: false },
        { text: 'Versand', value: 'shipping', sortable: false, width: '100px' },
        { text: 'Datum', value: 'createDate', width: '150px' },
        { text: 'Aktionen', value: 'actions', sortable: false, align: 'center', width: '120px' }
      ],
      filters: {
        startDate: null,
        endDate: null,
        errorType: null,
        shop: null
      },
      startDateMenu: false,
      endDateMenu: false,
      errorTypes: [
        { text: 'Alle Fehler', value: null },
        { text: 'Adressprobleme', value: 'address' },
        { text: 'Gewichtsprobleme', value: 'weight' },
        { text: 'Profilprobleme', value: 'profile' },
        { text: 'API-Probleme', value: 'api' }
      ],
      shops: [
        { text: 'Alle', value: null },
        { text: 'Amazon', value: 'amazon' },
        { text: 'Shopify', value: 'shopify' },
        { text: 'ACS', value: 'acs' },
        { text: 'Muckelmeister', value: 'muckelmeister' },
        { text: 'Etsy', value: 'etsy' }
      ],
      quickRetryDialog: false,
      retryOrder: null,
      retrying: false,
      selectedOrders: [],
      bulkRetryDialog: false,
      bulkRetrying: false,
      bulkRetryProgress: {
        current: 0,
        total: 0,
        currentOrder: null,
        results: []
      }
    };
  },
  watch: {
    options: {
      handler() {
        if (!this.isInitialLoad) {
          this.updateUrlParams();
          this.loadOrders();
        }
      },
      deep: true
    },
    filters: {
      handler() {
        if (!this.isInitialLoad) {
          this.updateUrlParams();
        }
      },
      deep: true
    }
  },
  mounted() {
    // Prevent duplicate initial load
    this.isInitialLoad = true;
    this.loadFiltersFromUrl();
    this.loadOrders();
    this.$nextTick(() => {
      this.isInitialLoad = false;
    });
  },
  computed: {
    bulkRetryProgressPercent() {
      if (this.bulkRetryProgress.total === 0) return 0;
      return (this.bulkRetryProgress.current / this.bulkRetryProgress.total) * 100;
    }
  },
  methods: {
    async loadOrders() {
      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;
        const params = {
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
          sortBy: sortBy[0] || 'createDate',
          sortOrder: sortDesc[0] ? 'desc' : 'asc'
        };

        // Add filters
        if (this.filters.startDate) {
          params.startDate = this.filters.startDate;
        }
        if (this.filters.endDate) {
          params.endDate = this.filters.endDate;
        }
        if (this.filters.shop) {
          params.shop = this.filters.shop;
        }

        const response = await FailedLabelsService.getFailedLabels(params);
        
        if (response.data.success) {
          this.orders = response.data.data;
          this.totalOrders = response.data.pagination.total;
        }
      } catch (error) {
        console.error('Error loading failed labels:', error);
        this.$root.$emit('showError', 'Fehler beim Laden der Bestellungen');
      } finally {
        this.loading = false;
      }
    },

    refreshData() {
      this.loadOrders();
    },

    applyFilters() {
      this.options.page = 1; // Reset to first page
      this.updateUrlParams();
      this.loadOrders();
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    viewDetails(order) {
      this.$router.push(`/failed-labels/${encodeURIComponent(order.orderId)}`);
    },

    retryLabel(order) {
      this.retryOrder = order;
      this.quickRetryDialog = true;
    },

    async confirmQuickRetry() {
      if (!this.retryOrder) return;
      
      this.retrying = true;
      try {
        const response = await FailedLabelsService.retryLabelCreation(this.retryOrder.orderId, {});
        
        if (response.data.success) {
          this.$root.$emit('showSuccess', 'Label erfolgreich erstellt');
          this.quickRetryDialog = false;
          this.loadOrders();
        } else {
          throw new Error(response.data.message || 'Retry failed');
        }
      } catch (error) {
        console.error('Error retrying label:', error);
        this.$root.$emit('showError', error.response?.data?.message || 'Fehler beim erneuten Versuch der Label-Erstellung');
      } finally {
        this.retrying = false;
      }
    },

    getShopColor(shop) {
      const colors = {
        amazon: 'orange',
        shopify: 'green',
        acs: 'blue',
        muckelmeister: 'purple',
        etsy: 'deep-orange',
        unknown: 'grey'
      };
      return colors[shop?.toLowerCase()] || colors.unknown;
    },

    loadFiltersFromUrl() {
      const query = this.$route.query;
      
      // Load filters from URL
      if (query.startDate) {
        this.filters.startDate = query.startDate;
      }
      if (query.endDate) {
        this.filters.endDate = query.endDate;
      }
      if (query.errorType) {
        this.filters.errorType = query.errorType;
      }
      if (query.shop) {
        this.filters.shop = query.shop;
      }
      
      // Load pagination from URL
      if (query.page) {
        this.options.page = parseInt(query.page, 10);
      }
      if (query.itemsPerPage) {
        this.options.itemsPerPage = parseInt(query.itemsPerPage, 10);
      }
      if (query.sortBy) {
        this.options.sortBy = [query.sortBy];
      }
      if (query.sortDesc !== undefined) {
        this.options.sortDesc = [query.sortDesc === 'true'];
      }
    },

    updateUrlParams() {
      const query = {};
      
      // Add filters to query
      if (this.filters.startDate) {
        query.startDate = this.filters.startDate;
      }
      if (this.filters.endDate) {
        query.endDate = this.filters.endDate;
      }
      if (this.filters.errorType) {
        query.errorType = this.filters.errorType;
      }
      if (this.filters.shop) {
        query.shop = this.filters.shop;
      }
      
      // Add pagination to query
      if (this.options.page !== 1) {
        query.page = this.options.page;
      }
      if (this.options.itemsPerPage !== 25) {
        query.itemsPerPage = this.options.itemsPerPage;
      }
      if (this.options.sortBy[0] !== 'createDate') {
        query.sortBy = this.options.sortBy[0];
      }
      if (this.options.sortDesc[0] !== true) {
        query.sortDesc = this.options.sortDesc[0];
      }
      
      // Update URL without triggering navigation
      this.$router.replace({ query }).catch(() => {});
    },

    clearFilters() {
      // Reset filters
      this.filters = {
        startDate: null,
        endDate: null,
        errorType: null,
        shop: null
      };
      
      // Reset pagination
      this.options.page = 1;
      
      // Clear URL params and reload
      this.$router.replace({ query: {} }).catch(() => {});
      this.loadOrders();
    },

    bulkRetryLabels() {
      if (this.selectedOrders.length === 0) return;
      
      this.bulkRetryDialog = true;
      this.bulkRetrying = true;
      this.bulkRetryProgress = {
        current: 0,
        total: this.selectedOrders.length,
        currentOrder: null,
        results: []
      };
      
      this.processBulkRetry();
    },

    async processBulkRetry() {
      for (let i = 0; i < this.selectedOrders.length; i++) {
        const order = this.selectedOrders[i];
        this.bulkRetryProgress.currentOrder = order;
        
        try {
          const response = await FailedLabelsService.retryLabelCreation(order.orderId, {});
          
          if (response.data.success) {
            this.bulkRetryProgress.results.push({
              orderId: order.orderId,
              success: true
            });
          } else {
            this.bulkRetryProgress.results.push({
              orderId: order.orderId,
              success: false,
              error: response.data.message || 'Unbekannter Fehler'
            });
          }
        } catch (error) {
          this.bulkRetryProgress.results.push({
            orderId: order.orderId,
            success: false,
            error: error.response?.data?.message || 'Verbindungsfehler'
          });
        }
        
        this.bulkRetryProgress.current = i + 1;
      }
      
      this.bulkRetrying = false;
      this.bulkRetryProgress.currentOrder = null;
      
      // Show summary
      const successCount = this.bulkRetryProgress.results.filter(r => r.success).length;
      const failedCount = this.bulkRetryProgress.results.filter(r => !r.success).length;
      
      if (successCount > 0) {
        this.$root.$emit('showSuccess', `${successCount} Label erfolgreich erstellt`);
      }
      if (failedCount > 0) {
        this.$root.$emit('showWarning', `${failedCount} Label konnten nicht erstellt werden`);
      }
      
      // Reload orders and clear selection
      this.loadOrders();
      this.selectedOrders = [];
    },

    closeBulkRetryDialog() {
      if (!this.bulkRetrying) {
        this.bulkRetryDialog = false;
        this.bulkRetryProgress = {
          current: 0,
          total: 0,
          currentOrder: null,
          results: []
        };
      }
    }
  }
};
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>